import { Lesson, MODULE_TYPES, PRESENTATION_VALUES } from "@equipped/core/core/manifest";
import { FieldDescriptor } from "./fieldTypes";

/**
 * The Lesson properties panel's field registry — EVERY field of `Lesson`
 * (`packages/core/core/manifest.ts`), for EVERY `type` (docs/343 v0 §1's load-
 * bearing finding: `LessonSchema` is one flat object, not a discriminated union
 * per module type — one registry covers all 17). `Lesson` is `z.infer<typeof
 * LessonSchema>` (not a hand-maintained interface), so `keyof Lesson` is trustworthy
 * — unlike the catalog-side types, see `catalogPackEntryFields.ts`'s own note about
 * a real drift discovered there.
 */
export const lessonFields = {
  id: { section: "Identity", label: "Lesson id", kind: "text" },
  title: { section: "Identity", label: "Title", kind: "text" },
  type: {
    section: "Identity",
    label: "Type",
    kind: "select",
    options: [
      ...MODULE_TYPES.map((t) => ({ value: t, label: t })),
      { value: "unknown", label: "unknown (unrecognized by this app build)" },
    ],
  },
  file: {
    section: "Identity",
    label: "File",
    kind: "text",
    filePicker: true,
    help: "Pack-relative path to this lesson's own content file (markdown or a module-type JSON body).",
  },
  optional: { section: "Identity", label: "Optional (doesn't count toward completion)", kind: "boolean3" },

  media: { section: "Media & narration", label: "Media path/URL", kind: "text", filePicker: true },
  duration: { section: "Media & narration", label: "Duration (seconds)", kind: "number" },
  narration: { section: "Media & narration", label: "Narration audio path", kind: "text", filePicker: true },
  captions: { section: "Media & narration", label: "Captions (WebVTT) path", kind: "text", filePicker: true },
  readalong: { section: "Media & narration", label: "Read-along timing sidecar path", kind: "text", filePicker: true },
  narrations: { section: "Media & narration", label: "Narration tracks", kind: "narrationTracks" },

  subtitle: { section: "Content", label: "Subtitle", kind: "text" },
  description: { section: "Content", label: "Description", kind: "textarea" },
  languages: { section: "Content", label: "Languages this lesson ships", kind: "stringArray" },
  updated: { section: "Content", label: "Updated (YYYY-MM-DD)", kind: "text" },
  contentHash: { section: "Content", label: "Content hash", kind: "text" },
  tags: { section: "Content", label: "Tags", kind: "stringArray" },
  related: { section: "Content", label: "Related lesson ids", kind: "stringArray" },
  presentation: {
    section: "Content",
    label: "Presentation hint",
    kind: "select",
    options: PRESENTATION_VALUES.map((p) => ({ value: p, label: p })),
    help: "A label/icon hint for a reading lesson — never changes which renderer runs.",
  },

  features: { section: "Access & capabilities", label: "Feature flags", kind: "featureFlags" },
  visibility: {
    section: "Access & capabilities",
    label: "Visibility",
    kind: "select",
    options: [
      { value: "public", label: "public" },
      { value: "private", label: "private" },
      { value: "hidden", label: "hidden" },
    ],
  },
  platforms: { section: "Access & capabilities", label: "Platform availability (opt-out)", kind: "recordBoolean" },
  castable: { section: "Access & capabilities", label: "Castable (AirPlay/Chromecast)", kind: "boolean3" },

  url: { section: "Link (type: link)", label: "URL", kind: "text" },
  openMode: {
    section: "Link (type: link)",
    label: "Open mode",
    kind: "select",
    options: [
      { value: "auto", label: "auto" },
      { value: "embed", label: "embed" },
      { value: "external", label: "external" },
    ],
  },
  showUrl: { section: "Link (type: link)", label: "Show the URL under the title", kind: "boolean3" },
  source: {
    section: "Link (type: link)",
    label: "Source hint",
    kind: "select",
    options: [
      { value: "youtube", label: "youtube" },
      { value: "vimeo", label: "vimeo" },
      { value: "web", label: "web" },
    ],
  },

  raw: {
    section: "Compatibility",
    label: "Raw (unrecognized) lesson data",
    kind: "readonly",
    help: 'Present only when type is "unknown" — the original authored object, preserved so a future app build (or a human) can still read it. Not an authoring field.',
  },
} satisfies Record<keyof Lesson, FieldDescriptor>;
